import React, { useEffect, useState, useRef, useCallback } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  PlusCircle,
  Loader2,
  UserCircle2,
  X,
  Upload,
  ChevronRight,
  Download,
  Film,
  Image,
  Send,
  Paperclip,
  Mic,
  FileText,
  Music,
  Play,
  Pause,
  Volume2,
  VolumeX,
} from "lucide-react";
import { MdArrowDropDown, MdOutlineArrowDropUp } from "react-icons/md";
import { format } from "date-fns";
import { BsArrowRightShort } from "react-icons/bs";
import axios from "axios";
import { BASE_URL, environment } from "utils/constant";
import { Receipt, Wallet, Lock, Scale } from "lucide-react";
import { LuUser } from "react-icons/lu";
import { rtdb, firebase, getToken, sendPushNotification } from "utils/firebase";
import { ProjectApi } from "api/projects/service";
import { SnagObjectsAPI } from "api/dashboard/service";
import { use } from "react";
import { fileTypeIcons } from "utils/utilityFunctions";
import CustomAudioPlayer from "components/CustomAudioPlayer";
import { convertToWav, formatDuration } from "utils/audioUtils";

const formatDisplayTime = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};


// Date Separator Component
const DateSeparator = ({ date }) => {
  const formatDateSeparator = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const messageDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
    const todayDate = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    const yesterdayDate = new Date(
      yesterday.getFullYear(),
      yesterday.getMonth(),
      yesterday.getDate()
    );

    if (messageDate.getTime() === todayDate.getTime()) {
      return "Today";
    } else if (messageDate.getTime() === yesterdayDate.getTime()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }
  };

  return (
    <div className="flex items-center justify-center my-4">
      <div className="flex-grow border-t border-gray-300"></div>
      <span className="px-4 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
        {formatDateSeparator(date)}
      </span>
      <div className="flex-grow border-t border-gray-300"></div>
    </div>
  );
};

const CompanyCommunications = () => {
  const [newMessage, setNewMessage] = useState("");
  const [isGlobal, setIsGlobal] = useState(true);
  const [mediaFile, setMediaFile] = useState(null);
  const fileInputRef = useRef(null);
  const messageInputRef = useRef(null);
  const [formMode, setFormMode] = useState(null);
  const [activeComment, setActiveComment] = useState(null);
  const queryClient = useQueryClient();
  const organization = localStorage.getItem("organization");
  const [organizationId, setOrganizationId] = useState(
    JSON.parse(organization)?.id || null
  );
  const accessToken = localStorage.getItem("access");
  const currentUser = JSON.parse(localStorage.getItem("user"));
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [showSiteDropdown, setShowSiteDropdown] = useState(false);
  const siteDropdownButtonRef = useRef(null);
  const siteDropdownRef = useRef(null);
  const [mentionQuery, setMentionQuery] = useState("");
  const [showMentions, setShowMentions] = useState(false);
  const [mentionPosition, setMentionPosition] = useState(null);
  const [messages, setMessages] = useState([]);
  const [globalMessages, setGlobalMessages] = useState([]);
  const [globalSnags, setGlobalSnags] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [viewingFile, setViewingFile] = useState(null);
  const [sites, setSites] = useState([]);
  const [isSitesLoading, setIsSitesLoading] = useState(false);
  const [selectedSnagRoom, setSelectedSnagRoom] = useState("general updates");
  const [selectedGlobalSnagRoom, setSelectedGlobalSnagRoom] = useState({
    id: "general_updates",
    title: "General Updates",
  });
  const [showSnagDropdown, setShowSnagDropdown] = useState(false);
  const [organizationTeams, setOrganizationTeams] = useState([]);
  const [snagRooms, setSnagRooms] = useState([]);
  const [replyTo, setReplyTo] = useState(null); // { id, senderName, siteName, chatroomName, text }
  const messagesEndRef = useRef(null);

  // Audio recording state
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const timerRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        }
      });

      // Use the best available audio format
      const options = {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 128000
      };

      // Fallback if webm is not supported
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = 'audio/webm';
        if (!MediaRecorder.isTypeSupported(options.mimeType)) {
          options.mimeType = ''; // Let browser choose
        }
      }

      const recorder = new MediaRecorder(stream, options);
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = async () => {
        try {
          // Create blob from chunks
          const mimeType = recorder.mimeType || 'audio/webm';
          const blob = new Blob(chunks, { type: mimeType });

          console.log('Original recording:', {
            size: blob.size,
            type: blob.type
          });

          // Convert to proper WAV format
          const wavBlob = await convertToWav(blob);

          console.log('Converted to WAV:', {
            size: wavBlob.size,
            type: wavBlob.type
          });

          const audioFile = new File([wavBlob], "recording.wav", {
            type: "audio/wav",
          });

          setMediaFile(audioFile);

          // Stop all tracks
          const tracks = stream.getTracks();
          tracks.forEach((track) => track.stop());
        } catch (error) {
          console.error("Error converting audio to WAV:", error);
          alert("Failed to process recording. Please try again.");

          // Stop all tracks
          const tracks = stream.getTracks();
          tracks.forEach((track) => track.stop());
        }
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);

      // Start timer
      setRecordingDuration(0);
      timerRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      alert("Could not access microphone. Please check permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  useEffect(() => {
    const orgId = JSON.parse(localStorage.getItem("organization"))?.id;
    setOrganizationId(orgId);
  }, [localStorage.getItem("organization")]);

  const sendNotificationToTeam = async (data, siteId) => {
    // setLoading(true);
    try {
      const accessToken = localStorage.getItem("access");
      var requestOptions = {
        method: "GET",
        headers: new Headers({
          Authorization: `Bearer ${accessToken}`,
        }),
        redirect: "follow",
      };

      // let res = await fetch(
      //   `${BASE_URL}/team/list-site-members/${siteId}/`,
      //   requestOptions
      // );
      // if (!res?.ok) throw new Error(res?.message ?? "Something went wrong");
      const result = await ProjectApi.listSiteMembers(siteId);
      if (!result?.status) {
        throw new Error(result?.message ?? "Something went wrong");
      }
      result?.data?.map(async (item) => {
        const member_token = await getToken(item?.user_id);
        if (member_token?.token) {
          // console.log(
          //   "Sending notification to:",
          //   item?.user_id,
          //   member_token?.token
          // );
          sendPushNotification(member_token?.token, data?.title, data?.body);
        }
      });
    } catch (error) {
      console.log(error);
    }
  };

  const sendNotificationToOrgTeam = async (data) => {
    // setLoading(true);
    try {
      if (!organizationId || organizationTeams) return;
      organizationTeams.forEach(async (item) => {
        const member_token = await getToken(item?.user?.id);
        if (member_token?.token) {
          sendPushNotification(member_token?.token, data?.title, data?.body);
        }
      });
    } catch (error) {
      console.log(error);
    }
  };
  // Optimized image URL fetching with React Query
  const getImageUrl = useCallback(
    async (mediaId) => {
      if (!mediaId) return null;
      try {
        const data = await SnagObjectsAPI.getObjectById(mediaId);
        if (data.status) {
          return data.data;
        } else {
          console.error("Failed to fetch media URL:", data.message);
          return null;
        }
      } catch (error) {
        console.error("Error fetching media URL:", error);
        return null;
      }
    },
    [accessToken]
  );

  // React Query hook for image URLs with caching
  const useImageUrl = (mediaId) => {
    return useQuery({
      queryKey: ["imageUrl", mediaId],
      queryFn: () => getImageUrl(mediaId),
      enabled: !!mediaId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      retry: 2,
    });
  };

  // Close site dropdown on outside click
  // useEffect(() => {
  //   const handleClickOutside = (event) => {
  //     if (!showSiteDropdown) return;
  //     const clickedInsideDropdown = siteDropdownRef.current?.contains(
  //       event.target
  //     );
  //     const clickedOnButton = siteDropdownButtonRef.current?.contains(
  //       event.target
  //     );
  //     if (!clickedInsideDropdown && !clickedOnButton) {
  //       setShowSiteDropdown(false);
  //     }
  //   };
  //   document.addEventListener("mousedown", handleClickOutside);
  //   return () => document.removeEventListener("mousedown", handleClickOutside);
  // }, [showSiteDropdown]);

  useEffect(() => {
    if (!organizationId) return;

    const fetchData = async () => {
      const sitesResp = await ProjectApi.listSitesByOrganization(
        organizationId
      );
      const siteList = Array.isArray(sitesResp)
        ? sitesResp
        : sitesResp?.data || [];
      setSites(siteList);
    };

    fetchData().catch(console.error);
  }, [organizationId]);

  // Fetch organization teams
  useEffect(() => {
    if (!organizationId) return;

    const fetchTeams = async () => {
      const teamsResp = await fetch(
        `${BASE_URL}/org/organization/${organizationId}/members/`
      );
      const data = await teamsResp.json();
      if (data?.status) {
        setOrganizationTeams(data?.data || []);
      } else {
        console.error("Failed to fetch organization teams:", data?.message);
      }
    };

    fetchTeams().catch(console.error);
  }, [organizationId]);

  // useeffect to fetch snag rooms when selected site changes
  useEffect(() => {
    if ((!organizationId || !selectedSite) && !sites.length) return;
    let snagsRef; // keep a reference so we can cleanup
    let mounted = true;
    const fetchSnagRooms = async () => {
      snagsRef = rtdb.ref(
        `site_snags-${environment}/${selectedSite.id ?? sites[0]?.id}`
      );
      snagsRef.on("value", (snapshot) => {
        const rooms = [];
        snapshot.forEach((child) => {
          const key = child.key;
          const data = child.val();
          rooms.push({
            id: key,
            title:
              key === "general_updates"
                ? "General Updates"
                : data?.title || key,
          });
        });
        // console.log("Fetched snag rooms for selected site:", rooms);
        if (!mounted) return;
        setSnagRooms(rooms);
      });
    };
    fetchSnagRooms().catch(console.error);
    return () => {
      mounted = false;
      if (snagsRef) snagsRef.off();
    };
  }, [organizationId, selectedSite]);

  //useEffect to fetch global snags
  useEffect(() => {
    if (!organizationId) return;
    let snagsRef;
    let mounted = true;
    const fetchGlobalSnags = async () => {
      snagsRef = rtdb.ref(`global_snags-${environment}/${organizationId}`);
      snagsRef.on("value", (snapshot) => {
        const rooms = [];
        snapshot.forEach((child) => {
          const key = child.key;
          const data = child.val();
          rooms.push({
            id: key,
            title:
              key === "general_updates"
                ? "General Updates"
                : data?.title || key,
          });
        });
        // console.log("Fetched global snag rooms:", rooms);
        if (!mounted) return;
        setGlobalSnags(rooms);
      });
    };
    fetchGlobalSnags().catch(console.error);
    return () => {
      mounted = false;
      if (snagsRef) snagsRef.off();
    };
  }, [organizationId]);

  // Media upload mutation
  const uploadMediaMutation = useMutation({
    mutationFn: async ({ file, siteId, organizationId }) => {
      const formData = new FormData();
      if (siteId) formData.append("site_id", siteId);
      if (organizationId) formData.append("organization_id", organizationId); // Assuming API needs this for global
      formData.append("snag_id", "general_updates");

      let type = "document";
      if (file.type.startsWith("image/")) type = "image";
      else if (file.type.startsWith("video/")) type = "video";
      else if (file.type.startsWith("audio/")) type = "audio";

      formData.append("type", type);

      if (type === "image") {
        formData.append("image", file);
      } else {
        formData.append("file", file);
      }

      const response = await axios.post(
        `${BASE_URL}/core/uploadsnagobjects/`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.data.status) {
        throw new Error("Failed to upload media");
      }

      return { ...response.data, mediaType: type };
    },
    onSuccess: (data) => {
      // Prefetch the uploaded media data
      if (data.data) {
        queryClient.setQueryData(["imageUrl", data.data.id], data.data);

        // If it's an audio file, refresh after 10 seconds to get transcription
        if (data.mediaType === "audio") {
          setTimeout(() => {
            queryClient.invalidateQueries(["imageUrl", data.data.id]);
          }, 10000);
        }
      }
    },
  });

  // Optimized Firebase message fetching
  useEffect(() => {
    if (!organizationId || !sites.length) return;

    const activeRefs = [];

    const fetchMessagesForSite = (site) => {
      const siteId = site.id;
      const snagsRef = rtdb.ref(`site_snags-${environment}/${siteId}`);
      activeRefs.push(snagsRef);

      snagsRef.on("value", async (snapshot) => {
        const snagsData = [];
        snapshot.forEach((childSnapshot) => {
          const key = childSnapshot.key;
          const snagData = childSnapshot.val();
          snagsData.push({
            id: key,
            title:
              key === "general_updates"
                ? "General Updates"
                : snagData.title || key,
            ...snagData,
          });
        });

        // Fetch messages for each snag in this site
        for (const snag of snagsData) {
          const messagesRef = rtdb.ref(
            `site_snags-${environment}/${siteId}/${snag.id}/messages`
          );
          activeRefs.push(messagesRef);

          messagesRef
            .orderByChild("createdAt")
            .limitToLast(50) // Limit messages for better performance
            .on("value", async (msgSnapshot) => {
              const snagMessages = [];
              msgSnapshot.forEach((childSnapshot) => {
                snagMessages.push({
                  id: childSnapshot.key,
                  siteId,
                  siteName: site.site_name,
                  chatroomId: snag.id,
                  chatroomName: snag.title || "General Updates",
                  ...childSnapshot.val(),
                });
              });

              // Prefetch image URLs for messages with media
              snagMessages.forEach((msg) => {
                if (msg.has_media && msg.media) {
                  if (Array.isArray(msg.media)) {
                    msg.media.forEach((mediaItem) => {
                      if (mediaItem.mediaId) {
                        // Prefetch image URL
                        queryClient.prefetchQuery({
                          queryKey: ["imageUrl", mediaItem.mediaId],
                          queryFn: () => getImageUrl(mediaItem.mediaId),
                          staleTime: 5 * 60 * 1000,
                        });
                      }
                    });
                  } else if (msg.media.mediaId) {
                    // Prefetch image URL
                    queryClient.prefetchQuery({
                      queryKey: ["imageUrl", msg.media.mediaId],
                      queryFn: () => getImageUrl(msg.media.mediaId),
                      staleTime: 5 * 60 * 1000,
                    });
                  }
                }
              });

              // Update messages with debouncing for better performance
              setMessages((prevMessages) => {
                const filteredMessages = prevMessages.filter(
                  (m) => !(m.siteId === siteId && m.chatroomId === snag.id)
                );
                const updatedMessages = [...filteredMessages, ...snagMessages];
                return updatedMessages.sort(
                  (a, b) => a.createdAt - b.createdAt
                );
              });
            });
        }
      });
    };

    // Clear messages when switching sites
    setMessages([]);

    // Fetch messages based on selected site or all sites
    if (selectedSite) {
      fetchMessagesForSite(selectedSite);
    } else {
      // Limit to first 5 sites for performance if showing all
      const limitedSites = sites.slice(0, 5);
      for (const site of limitedSites) {
        fetchMessagesForSite(site);
      }
    }

    // Cleanup function
    return () => {
      activeRefs.forEach((ref) => ref.off());
    };
  }, [organizationId, selectedSite, sites, queryClient, getImageUrl]);

  // Fetch global snags messages
  useEffect(() => {
    if (!organizationId) return;

    const activeRefs = [];

    const fetchMessagesForGlobal = () => {
      const snagsRef = rtdb.ref(
        `global_snags-${environment}/${organizationId}`
      );
      activeRefs.push(snagsRef);

      snagsRef.on("value", async (snapshot) => {
        const snagsData = [];
        snapshot.forEach((childSnapshot) => {
          const key = childSnapshot.key;
          const snagData = childSnapshot.val();
          snagsData.push({
            id: key,
            title:
              key === "general_updates"
                ? "General Updates"
                : snagData.title || key,
            ...snagData,
          });
        });

        // Fetch messages for each snag in this site
        for (const snag of snagsData) {
          const messagesRef = rtdb.ref(
            `global_snags-${environment}/${organizationId}/${snag.id}/messages`
          );
          activeRefs.push(messagesRef);

          messagesRef
            .orderByChild("createdAt")
            .limitToLast(50) // Limit messages for better performance
            .on("value", async (msgSnapshot) => {
              const snagMessages = [];
              msgSnapshot.forEach((childSnapshot) => {
                snagMessages.push({
                  id: childSnapshot.key,
                  siteName: "Global",
                  chatroomId: snag.id,
                  chatroomName: snag.title || "General Updates",
                  ...childSnapshot.val(),
                });
              });

              // Prefetch image URLs for messages with media
              snagMessages.forEach((msg) => {
                if (msg.has_media && msg.media) {
                  if (Array.isArray(msg.media)) {
                    msg.media.forEach((mediaItem) => {
                      if (mediaItem.mediaId) {
                        // Prefetch image URL
                        queryClient.prefetchQuery({
                          queryKey: ["imageUrl", mediaItem.mediaId],
                          queryFn: () => getImageUrl(mediaItem.mediaId),
                          staleTime: 5 * 60 * 1000,
                        });
                      }
                    });
                  } else if (msg.media.mediaId) {
                    // Prefetch image URL
                    queryClient.prefetchQuery({
                      queryKey: ["imageUrl", msg.media.mediaId],
                      queryFn: () => getImageUrl(msg.media.mediaId),
                      staleTime: 5 * 60 * 1000,
                    });
                  }
                }
              });

              // Update messages with debouncing for better performance
              setGlobalMessages((prevMessages) => {
                const filteredMessages = prevMessages.filter(
                  (m) => !(m.chatroomId === snag.id)
                );
                const updatedMessages = [...filteredMessages, ...snagMessages];
                return updatedMessages.sort(
                  (a, b) => a.createdAt - b.createdAt
                );
              });
            });
        }
      });
    };

    // Clear messages when switching sites
    setGlobalMessages([]);

    fetchMessagesForGlobal();

    // Cleanup function
    return () => {
      activeRefs.forEach((ref) => ref.off());
    };
  }, [organizationId, queryClient, getImageUrl]);

  // Optimized auto-scroll with debouncing
  useEffect(() => {
    if (messages.length > 0) {
      const timer = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [messages.length]);

  useEffect(() => {
    if (globalMessages.length > 0) {
      const timer = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [globalMessages.length]);

  // Optimized message sending
  const handleSendMessage = async () => {
    if (!newMessage.trim() && !mediaFile) return;

    let mediaUrl = null;
    let mediaType = null;
    let mediaId = null;

    const siteId = replyTo?.siteId || selectedSite?.id || sites[0]?.id;
    const snagRoomId =
      replyTo?.chatroomId || selectedSnagRoom?.id || "general_updates";

    if (mediaFile) {
      try {
        setIsUploading(true);
        const result = await uploadMediaMutation.mutateAsync({
          file: mediaFile,
          siteId,
        });

        // Determine URL and Type based on response or file type
        // Assuming backend returns 'image' field for images and 'file' for others, or we use the one we sent
        // But for consistency let's check the type we determined
        mediaType = result.mediaType;
        mediaUrl = result.data.image || result.data.file; // Adjust based on actual API response structure
        mediaId = result.data.id;
      } catch (error) {
        console.error("Error uploading media:", error);
        return;
      } finally {
        setIsUploading(false);
        setMediaFile(null);
      }
    }

    const message = {
      text: newMessage,
      createdAt: firebase.database.ServerValue.TIMESTAMP,
      senderId: currentUser?.user,
      senderName: `${currentUser?.first_name} ${currentUser?.last_name}` || "Anonymous",
      media: mediaUrl ? [{ url: mediaUrl, type: mediaType, mediaId }] : null,
      has_media: !!mediaUrl,
      ...(replyTo
        ? {
          replyTo: {
            has_media: false,
            senderName: replyTo?.senderName || null,
            text: replyTo?.text || null,
          },
        }
        : {}),
    };


    try {
      await rtdb
        .ref(`site_snags-${environment}/${siteId}/${snagRoomId}/messages`)
        .push(message);

      setNewMessage("");
      setReplyTo(null);

      const notificationData = {
        title: `New message in ${snagRoomId === "general updates" ? "General Updates" : snagRoomId
          }@${selectedSite?.site_name || "All Projects"}`,
        body: `${currentUser?.first_name} ${currentUser?.last_name
          }: ${newMessage}${mediaUrl ? ". Media attached." : ""}`,
        data: {
          type: "snag_message",
          snagId: snagRoomId,
          siteId,
          senderId: currentUser?.user,
        },
      };
      await sendNotificationToTeam(notificationData, siteId);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const handleSendGlobalMessage = async () => {
    if (!newMessage.trim() && !mediaFile) return;

    let mediaUrl = null;
    let mediaType = null;
    let mediaId = null;

    const snagRoomId =
      replyTo?.chatroomId || selectedGlobalSnagRoom?.id || "general_updates";

    if (mediaFile) {
      try {
        setIsUploading(true);
        const result = await uploadMediaMutation.mutateAsync({
          file: mediaFile,
          organizationId,
        });

        mediaType = result.mediaType;
        mediaUrl = result.data.image || result.data.file;
        mediaId = result.data.id;
      } catch (error) {
        console.error("Error uploading media:", error);
        return;
      } finally {
        setIsUploading(false);
        setMediaFile(null);
      }
    }

    const message = {
      text: newMessage,
      createdAt: firebase.database.ServerValue.TIMESTAMP,
      senderId: currentUser?.user,
      senderName:
        `${currentUser?.first_name} ${currentUser?.last_name}` || "Anonymous",
      media: mediaUrl ? [{ url: mediaUrl, type: mediaType, mediaId }] : null,
      has_media: !!mediaUrl,
      replyTo: {
        has_media: false,
        senderName: replyTo?.senderName || null,
        text: replyTo?.text || null,
      },
    };

    try {
      await rtdb
        .ref(
          `global_snags-${environment}/${organizationId}/${snagRoomId}/messages`
        )
        .push(message);

      setNewMessage("");
      setReplyTo(null);

      const notificationData = {
        title: `New message in Global@${snagRoomId === "general_updates"
          ? "General Updates"
          : selectedGlobalSnagRoom?.title
          }`,
        body: `${currentUser?.first_name} ${currentUser?.last_name
          }: ${newMessage}${mediaUrl ? ". Media attached." : ""}`,
        data: {
          type: "snag_message",
          snagId: snagRoomId,
          senderId: currentUser?.user,
        },
      };
      await sendNotificationToOrgTeam(notificationData);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  // Handle @ mentions
  const handleMessageChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);

    const lastAtIndex = value.lastIndexOf("@");
    if (
      lastAtIndex !== -1 &&
      (lastAtIndex === 0 || value[lastAtIndex - 1] === " ")
    ) {
      const query = value.substring(lastAtIndex + 1).split(" ")[0];
      setMentionQuery(query);

      const input = messageInputRef.current;
      const pos = input.getBoundingClientRect();
      setMentionPosition({
        bottom: window.innerHeight - pos.bottom + window.scrollY + 50,
        left: pos.left,
      });

      setShowMentions(true);
    } else {
      setShowMentions(false);
    }
  };

  const insertMention = (site) => {
    const lastAtIndex = newMessage.lastIndexOf("@");
    const newMessageText =
      newMessage.substring(0, lastAtIndex) +
      `@${site.site_name.replace(" ", "_")} ` +
      newMessage.substring(lastAtIndex + 1 + mentionQuery.length);
    setNewMessage(newMessageText);
    setShowMentions(false);
    messageInputRef.current.focus();
  };

  const filteredSites = mentionQuery
    ? sites.filter((site) =>
      site.site_name.toLowerCase().includes(mentionQuery.toLowerCase())
    )
    : sites;

  const handleFileSelection = (e) => {
    if (e.target.files && e.target.files[0]) {
      setMediaFile(e.target.files[0]);
    }
  };

  const isVideoUrl = (url) => {
    if (!url) return false;
    return (
      url.toLowerCase().endsWith(".mp4") ||
      url.toLowerCase().endsWith(".mov") ||
      url.toLowerCase().endsWith(".webm") ||
      url.toLowerCase().endsWith(".avi") ||
      /\.(mp4|mov|webm|avi)/i.test(url)
    );
  };

  const isImageUrl = (url) => {
    if (!url) return false;
    return (
      url.toLowerCase().endsWith(".jpg") ||
      url.toLowerCase().endsWith(".jpeg") ||
      url.toLowerCase().endsWith(".png") ||
      url.toLowerCase().endsWith(".gif") ||
      url.toLowerCase().endsWith(".webp") ||
      /\.(jpg|jpeg|png|gif|webp)/i.test(url)
    );
  };

  const ImageModal = ({ imageUrl, onClose }) => {
    const handleDownload = async () => {
      try {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = imageUrl.split("/").pop() || "media";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Download failed:", error);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full p-8 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
          <button
            onClick={handleDownload}
            className="absolute top-4 right-12 text-gray-500 hover:text-gray-700"
          >
            <Download className="w-6 h-6" />
          </button>
          {isVideoUrl(imageUrl) ? (
            <video
              src={imageUrl}
              controls
              className="w-full h-[500px] object-contain"
            />
          ) : (
            <img
              src={imageUrl}
              alt="Shared media"
              className="w-full h-[500px] object-contain"
            />
          )}
        </div>
      </div>
    );
  };

  const FileViewerModal = ({ fileUrl, fileName, onClose }) => {

    const handleDownload = async () => {
      try {
        const response = await fetch(fileUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName || "document";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Download failed:", error);
      }
    };

    const getFileExtension = (url) => {
      return url?.split(".").pop().split("?")[0].toLowerCase();
    };

    const extension = getFileExtension(fileUrl);
    const isDocx = extension === "docx" || extension === "doc";
    const isPdf = extension === "pdf";
    const isTxt = extension === "txt";

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-lg w-[60vw] h-[90vh] p-4 relative flex flex-col">
          <div className="flex justify-between items-center mb-4 border-b pb-2">
            <h3 className="text-lg font-semibold truncate max-w-[80%]">
              {fileName}
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDownload}
                className="p-2 hover:bg-gray-100 rounded-full text-gray-600"
                title="Download"
              >
                <Download className="w-5 h-5" />
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full text-gray-600"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex-1 bg-gray-100 rounded overflow-hidden relative">
            {isDocx ? (
              <iframe
                src={`https://docs.google.com/gview?url=${encodeURIComponent(
                  fileUrl
                )}&embedded=true`}
                className="w-full h-full border-0"
                title="Document Viewer"
              />
            ) : isPdf || isTxt ? (
              <iframe
                src={fileUrl}
                className="w-full h-full border-0"
                title="File Viewer"
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <FileText className="w-16 h-16 mb-4" />
                <p>Preview not available for this file type.</p>
                <button
                  onClick={handleDownload}
                  className="mt-4 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                >
                  Download File
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderTextWithLinks = (text) => {
    if (!text) return null;

    // Regex to identify URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;

    const parts = text.split(urlRegex);

    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline break-all"
            onClick={(e) => e.stopPropagation()}
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  // Optimized media rendering component
  const MediaRenderer = ({ media, isArray = false }) => {
    if (isArray) {
      return (
        <div className="flex flex-wrap gap-2 mb-2">
          {media.map((mediaItem) => (
            <MediaItem key={mediaItem.mediaId} mediaItem={mediaItem} />
          ))}
        </div>
      );
    } else {
      return (
        <div className="mb-2">
          <MediaItem mediaItem={media} />
        </div>
      );
    }
  };

  // Individual media item component with React Query
  const MediaItem = ({ mediaItem }) => {
    const { data: mediaData, isLoading, error } = useImageUrl(mediaItem.mediaId);

    if (isLoading) {
      return (
        <div className="h-20 w-20 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
        </div>
      );
    }

    if (error || !mediaData) {
      return (
        <div className="h-20 w-20 flex items-center justify-center bg-gray-200 rounded">
          <X className="w-6 h-6 text-gray-400" />
        </div>
      );
    }

    const url = mediaData.image || mediaData.file;
    let type = mediaData.type;
    if (isVideoUrl(url)) {
      type = "video";
    }
    else if (isImageUrl(url)) {
      type = "image";
    }

    if (type === "audio") {
      return (
        <CustomAudioPlayer url={url} transcription={mediaData.transcription} />
      );
    }

    if (type === "video" || isVideoUrl(url)) {
      return (
        <video
          src={url}
          controls
          className="rounded h-20 w-20 cursor-pointer object-cover bg-black"
        />
      );
    }

    if (type === "image" || (!type && url)) {
      return (
        <img
          src={url}
          alt="Shared media"
          className="rounded h-20 w-20 cursor-pointer object-cover border border-gray-200"
          onClick={() => {
            setSelectedImage(url);
            setIsFormOpen(true);
          }}
        />
      );
    }

    // Default for documents/PDFs/other
    const fileName = url ? url.split('?')[0].split('/').pop() : "Document";
    const extension = fileName.split(".").pop().toLowerCase();
    const isViewable = ["pdf", "txt", "docx", "doc"].includes(extension);


    return (
      <div className="flex items-center gap-2">
        <div
          onClick={() => {
            if (isViewable) {
              setViewingFile({ url, name: fileName });
            }
          }}
          className={`flex items-center gap-2 p-2 bg-gray-100 rounded border border-gray-200 max-w-[200px] ${isViewable ? "cursor-pointer hover:bg-gray-200" : ""
            } transition-colors`}
        >
          {/* <FileText className="w-5 h-5 text-purple-600 flex-shrink-0" /> */}
          {fileTypeIcons({ fileType: extension, className: "w-5 h-5 text-purple-600 flex-shrink-0" })}
          <span className="text-xs text-gray-700 truncate" title={fileName}>
            {fileName}
          </span>
        </div>
        <a
          href={url}
          download
          className="p-2 bg-gray-100 rounded border border-gray-200 hover:bg-gray-200 text-gray-600"
          title="Download"
        >
          <Download className="w-4 h-4" />
        </a>
      </div>
    );
  };

  return (
    <div className="h-[95%] w-[26vw] flex flex-col pb-2">
      {/* Fixed Header Section */}
      <div className="flex-shrink-0 px-3 pb-2 pt-3 rounded-t-lg bg-white border-t-[1px] border-[#CCCCCC] border-x sticky top-0 z-20">
        <div className="text-sm text-gray-500 flex flex-row items-center justify-between">
          <div className="rounded-xl border-[1px] flex flex-row text-sm items-center gap-1 border-[#95889C] relative">
            <div
              className={`px-2 flex flex-row items-center cursor-pointer justify-center rounded-xl transition-colors duration-300 ${isGlobal
                ? "bg-[#3B0069] text-white"
                : "text-purple-700 hover:bg-purple-100"
                }`}
              onClick={() => setIsGlobal(true)}
            >
              Global
            </div>
            <div
              className={`px-2 flex flex-row items-center cursor-pointer justify-center rounded-xl transition-colors duration-300 ${!isGlobal
                ? "bg-[#3B0069] text-white"
                : "text-purple-700 hover:bg-purple-100"
                }`}
              onClick={() => setIsGlobal(false)}
            >
              Projects
            </div>
          </div>
          <div className="relative">
            <button
              onClick={() => setShowSiteDropdown(!showSiteDropdown)}
              className="flex items-center border-[1px] px-2 py-1 border-[#95889C] justify-center rounded-lg text-purple-700 hover:text-purple-900"
              ref={siteDropdownButtonRef}
              style={{
                display: isGlobal ? "none" : "flex",
              }}
            >
              {showSiteDropdown ? (
                <MdOutlineArrowDropUp className="h-5 w-5 mr-1" />
              ) : (
                <MdArrowDropDown className="h-5 w-5 mr-1" />
              )}
              <div>
                {selectedSite ? selectedSite.site_name : "All Projects"}
              </div>
            </button>
            {/* {showSiteDropdown && (
              <div
                style={{
                  overflowY: "scroll",
                }}
                className="absolute right-0 mt-2 h-[50vh] w-48 bg-white rounded-lg shadow-md z-30 border border-gray-200"
                ref={siteDropdownRef}
              >
                <div
                  className="p-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => {
                    setMessages([]);
                    setSelectedSite(null);
                    setShowSiteDropdown(false);
                  }}
                >
                  All Projects
                </div>
                {sites.map((site) => (
                  <div
                    key={site.id}
                    className="p-2 hover:bg-gray-100 cursor-pointer"
                    onClick={() => {
                      setMessages([]);
                      setSelectedSite(site);
                      setShowSiteDropdown(false);
                    }}
                  >
                    {site.site_name}
                  </div>
                ))}
              </div>
            )} */}
          </div>
        </div>
      </div>

      {/* Scrollable Messages Container */}
      <div className="flex-1 px-3 bg-white border-x-[1px] border-[#CCCCCC] overflow-y-auto">
        {/* Mentions dropdown */}
        {showMentions && filteredSites.length > 0 && (
          <div
            className="absolute z-10 bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto"
            style={{
              bottom: mentionPosition?.bottom,
              minWidth: "200px",
            }}
          >
            {filteredSites.map((site) => (
              <div
                key={site.id}
                className="p-2 hover:bg-gray-100 cursor-pointer flex items-center"
                onClick={() => insertMention(site)}
              >
                <span>{site.site_name}</span>
              </div>
            ))}
          </div>
        )}

        {/* Messages Area */}
        {!isGlobal ? (
          <div className="space-y-4 py-4">
            {isSitesLoading ? (
              <div className="flex justify-center items-center p-2">
                <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center p-8 text-gray-500">
                No messages to show.
              </div>
            ) : (
              // only render messages that have a replyTo
              messages.map((msg, index) => {
                const isCurrentUser = msg.senderId === currentUser?.user;
                const previousMessage = index > 0 ? messages[index - 1] : null;
                const showDateSeparator =
                  !previousMessage ||
                  new Date(msg.createdAt).toDateString() !==
                  new Date(previousMessage?.createdAt).toDateString();
                const isReplyTarget = replyTo?.id === msg.id;

                return (
                  <React.Fragment key={msg.id}>
                    {showDateSeparator && (
                      <DateSeparator date={msg.createdAt} />
                    )}

                    <div
                      className={`mb-4 flex ${isCurrentUser ? "justify-end" : "justify-start"
                        }`}
                    >
                      <div
                        className={`rounded-lg w-[90%] p-3 ${isCurrentUser
                          ? "bg-purple-100 text-black"
                          : "bg-gray-100 text-gray-900"
                          } ${isReplyTarget ? "ring-2 ring-purple-500 ring-offset-1 ring-offset-white" : ""}`}
                      >
                        <div className="flex items-center gap-1 text-xs font-medium mb-2 flex-wrap">
                          <span className="font-semibold">
                            {msg.senderName}
                          </span>
                          <span
                            className={`px-2 py-[2px] rounded-full text-[11px] font-medium flex items-center gap-1 ${msg.chatroomName === "General Updates"
                              ? "bg-white text-purple-800"
                              : "bg-purple-400 text-white"
                              }`}
                          >
                            {msg.siteName} @{msg.chatroomName}
                          </span>
                        </div>

                        {/* Replied message preview */}
                        {msg.replyTo &&
                          (msg.replyTo.senderName || msg.replyTo.text) && (
                            <div className="mb-3 p-2 bg-white border-l-4 border-purple-600 rounded text-xs text-gray-700">
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <LuUser className="w-4 h-4 text-gray-500" />
                                  <span className="font-medium text-sm">
                                    {msg.replyTo.senderName || "Unknown"}
                                  </span>
                                </div>
                                <span className="text-[11px] text-gray-400">
                                  {msg.replyTo.has_media ? "Media" : "Message"}
                                </span>
                              </div>
                              <div className="text-sm text-gray-700 truncate">
                                {msg.replyTo.text ||
                                  (msg.replyTo.has_media ? "Attachment" : "")}
                              </div>
                            </div>
                          )}

                        {/* Current message media */}
                        {msg.has_media && msg.media && (
                          <MediaRenderer
                            media={msg.media}
                            isArray={Array.isArray(msg.media)}
                          />
                        )}

                        {/* Current message text */}
                        {msg.text && (
                          <div className="text-sm whitespace-pre-wrap break-words w-[18vw]">
                            {renderTextWithLinks(msg.text)}
                          </div>
                        )}

                        <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                          <span>{formatDisplayTime(msg.createdAt)}</span>
                          {!isCurrentUser && (
                            <button
                              onClick={() => {
                                setReplyTo({
                                  id: msg.id,
                                  senderName: msg.senderName,
                                  siteId: msg.siteId,
                                  siteName: msg.siteName,
                                  chatroomId: msg.chatroomId,
                                  chatroomName: msg.chatroomName,
                                  text: msg.text,
                                });
                                setSelectedSite(
                                  sites.find((s) => s.id === msg.siteId) || null
                                );
                                setSelectedSnagRoom({
                                  id: msg.chatroomId,
                                  title: msg.chatroomName,
                                });
                                messageInputRef.current?.focus();
                              }}
                              className="ml-2 text-purple-600 hover:underline"
                            >
                              Reply
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>
        ) : (
          <div className="space-y-4 py-4">
            {globalMessages.length === 0 ? (
              <div className="text-center p-8 text-gray-500">
                No messages to show.
              </div>
            ) : (
              // only render messages that have a replyTo
              globalMessages.map((msg, index) => {
                const isCurrentUser = msg.senderId === currentUser?.user;
                const previousMessage =
                  index > 0 ? globalMessages[index - 1] : null;
                const showDateSeparator =
                  !previousMessage ||
                  new Date(msg.createdAt).toDateString() !==
                  new Date(previousMessage?.createdAt).toDateString();
                const isReplyTarget = replyTo?.id === msg.id;

                return (
                  <React.Fragment key={msg.id}>
                    {showDateSeparator && (
                      <DateSeparator date={msg.createdAt} />
                    )}

                    <div
                      className={`mb-4 flex ${isCurrentUser ? "justify-end" : "justify-start"
                        }`}
                    >
                      <div
                        className={`rounded-lg w-[90%] p-3 ${isCurrentUser
                          ? "bg-purple-100 text-black"
                          : "bg-gray-100 text-gray-900"
                          } ${isReplyTarget ? "ring-2 ring-purple-500 ring-offset-1 ring-offset-white" : ""}`}
                      >
                        <div className="flex items-center gap-1 text-xs font-medium mb-2 flex-wrap">
                          <span className="font-semibold">
                            {msg.senderName}
                          </span>
                          <span
                            className={`px-2 py-[2px] rounded-full text-[11px] font-medium flex items-center gap-1 ${msg.chatroomName === "General Updates"
                              ? "bg-white text-purple-800"
                              : "bg-purple-400 text-white"
                              }`}
                          >
                            {msg.siteName} @{msg.chatroomName}
                          </span>
                        </div>

                        {/* Replied message preview */}
                        {msg.replyTo &&
                          (msg.replyTo.senderName || msg.replyTo.text) && (
                            <div className="mb-3 p-2 bg-white border-l-4 border-purple-600 rounded text-xs text-gray-700">
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <LuUser className="w-4 h-4 text-gray-500" />
                                  <span className="font-medium text-sm">
                                    {msg.replyTo.senderName || "Unknown"}
                                  </span>
                                </div>
                                <span className="text-[11px] text-gray-400">
                                  {msg.replyTo.has_media ? "Media" : "Message"}
                                </span>
                              </div>
                              <div className="text-sm text-gray-700 truncate">
                                {msg.replyTo.text ||
                                  (msg.replyTo.has_media ? "Attachment" : "")}
                              </div>
                            </div>
                          )}

                        {/* Current message media */}
                        {msg.has_media && msg.media && (
                          <MediaRenderer
                            media={msg.media}
                            isArray={Array.isArray(msg.media)}
                          />
                        )}

                        {/* Current message text */}
                        {msg.text && (
                          <div className="text-sm whitespace-pre-wrap break-words w-[18vw]">
                            {renderTextWithLinks(msg.text)}
                          </div>
                        )}

                        <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                          <span>{formatDisplayTime(msg.createdAt)}</span>
                          {!isCurrentUser && (
                            <button
                              onClick={() => {
                                setReplyTo({
                                  id: msg.id,
                                  senderName: msg.senderName,
                                  siteName: "Global",
                                  chatroomId: msg.chatroomId,
                                  chatroomName: msg.chatroomName,
                                  text: msg.text,
                                });
                                setSelectedGlobalSnagRoom({
                                  id: msg.chatroomId,
                                  title: msg.chatroomName,
                                });
                                messageInputRef.current?.focus();
                              }}
                              className="ml-2 text-purple-600 hover:underline"
                            >
                              Reply
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Fixed Input Section */}
      <div className="flex-shrink-0 bg-white border-[1px] border-[#CCCCCC] px-3 py-2 sticky bottom-0 z-20 rounded-b-lg">
        {mediaFile && (
          <div className="p-2 bg-gray-100 border-t flex items-center justify-between mb-2">
            <div className="flex items-center text-sm text-gray-600">
              {mediaFile.type.startsWith("image/") ? (
                <Image size={16} className="mr-2" />
              ) : mediaFile.type.startsWith("video/") ? (
                <Film size={16} className="mr-2" />
              ) : mediaFile.type.startsWith("audio/") ? (
                <Music size={16} className="mr-2" />
              ) : (
                <FileText size={16} className="mr-2" />
              )}
              {mediaFile.name} ({Math.round(mediaFile.size / 1024)} KB)
            </div>
            <button
              onClick={() => setMediaFile(null)}
              className="text-grey-500 hover:text-red-500"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* Snag Room Selector */}
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-1 text-xs font-medium">
            <span className="text-gray-600">Sending to:</span>
            <button
              onClick={() => {
                setShowSiteDropdown(!showSiteDropdown);
                // console.log("selected site:", selectedSite);
              }}
              disabled={isGlobal}
              className="flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs hover:bg-purple-200"
            >
              {isGlobal
                ? "Global"
                : selectedSite?.site_name ||
                sites[0]?.site_name ||
                "No Project Selected"}
              {!isGlobal && <MdArrowDropDown className="w-4 h-4" />}
            </button>
            <span className="text-gray-500">@</span>
            <button
              onClick={() => setShowSnagDropdown(!showSnagDropdown)}
              className="flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs hover:bg-purple-200"
            >
              {isGlobal
                ? selectedGlobalSnagRoom?.title || "general updates"
                : selectedSnagRoom?.title || "general updates"}
              <MdArrowDropDown className="w-4 h-4" />
            </button>
          </div>
          {/* {replyTo && (
            <button
              onClick={() => setReplyTo(null)}
              className="text-xs text-red-600 hover:text-red-800"
            >
              Cancel Reply
            </button>
          )} */}
        </div>

        {/* Site & Snag Dropdowns */}
        {showSiteDropdown && (
          <div className="absolute bottom-24 left-3 w-48 bg-white rounded-lg shadow-md z-30 border border-gray-200 max-h-48 overflow-y-auto">
            <div
              className="p-2 hover:bg-gray-100 cursor-pointer"
              onClick={() => {
                setSelectedSite(null);
                setShowSiteDropdown(false);
              }}
            >
              All Projects
            </div>
            {sites.map((site) => (
              <div
                key={site.id}
                className="p-2 hover:bg-gray-100 cursor-pointer"
                onClick={() => {
                  // console.log("selecting site:", site);
                  setSelectedSite(site);
                  setShowSiteDropdown(false);
                }}
              >
                {site.site_name}
              </div>
            ))}
          </div>
        )}
        {showSnagDropdown && selectedSite && !isGlobal && (
          <div className="absolute bottom-20 left-44 w-48 bg-white rounded-lg shadow-md z-30 border border-gray-200 max-h-48 overflow-y-auto">
            {/* <div
              className="p-2 hover:bg-gray-100 cursor-pointer"
              onClick={() => {
                setSelectedSnagRoom({
                  id: "general_updates",
                  title: "General Updates",
                });
                setShowSnagDropdown(false);
              }}
            >
              general updates
            </div> */}
            {snagRooms.map((room) => (
              <div
                key={room.id}
                className="p-2 hover:bg-gray-100 cursor-pointer"
                onClick={() => {
                  // console.log("snag rooms:", snagRooms);
                  setSelectedSnagRoom({
                    id: room.id,
                    title: room.title || room.id,
                  });
                  setShowSnagDropdown(false);
                }}
              >
                {room.title || room.id}
              </div>
            ))}
          </div>
        )}
        {showSnagDropdown && isGlobal && (
          <div className="absolute bottom-20 left-44 w-48 bg-white rounded-lg shadow-md z-30 border border-gray-200 max-h-48 overflow-y-auto">
            {globalSnags.map((room) => (
              <div
                key={room.id}
                className="p-2 hover:bg-gray-100 cursor-pointer"
                onClick={() => {
                  // console.log("snag rooms:", globalSnags);
                  setSelectedGlobalSnagRoom({
                    id: room.id,
                    title: room.title || room.id,
                  });
                  setShowSnagDropdown(false);
                }}
              >
                {room.title || room.id}
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center">
          {/* @ Button
          <button
            onClick={() => {
              setShowSiteDropdown(!showSiteDropdown);
              console.log("snag rooms:", snagRooms);
            }}
            className="p-2 text-purple-600 hover:bg-purple-100 rounded-full mr-2"
          >
            @
          </button> */}

          {/* Attachment Button */}
          <button
            onClick={() => fileInputRef.current.click()}
            className="p-2 text-gray-500 hover:bg-gray-100 rounded-full mr-2"
            disabled={isUploading || uploadMediaMutation.isLoading || isRecording}
          >
            <Paperclip size={20} />
          </button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelection}
            accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,audio/*"
            className="hidden"
          />

          {/* Mic Button */}
          <button
            onClick={isRecording ? stopRecording : startRecording}
            className={`p-2 rounded-full mr-2 ${isRecording
              ? "bg-red-100 text-red-600 animate-pulse"
              : "text-gray-500 hover:bg-gray-100"
              }`}
            disabled={isUploading || uploadMediaMutation.isLoading || (mediaFile && !isRecording)}
          >
            <Mic size={20} />
          </button>

          {/* Message Input */}
          <input
            ref={messageInputRef}
            type="text"
            className="flex-1 border rounded-l-lg px-3 py-2"
            placeholder={
              isUploading || uploadMediaMutation.isLoading
                ? "Uploading media..."
                : isRecording
                  ? `Recording... ${formatDuration(recordingDuration)}`
                  : "Type a message..."
            }
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                if (isGlobal) {
                  handleSendGlobalMessage();
                } else {
                  handleSendMessage();
                }
              }
            }}
            disabled={isUploading || uploadMediaMutation.isLoading}
          />

          {/* Send Button */}
          <button
            onClick={() => {
              if (isGlobal) {
                handleSendGlobalMessage();
              } else {
                handleSendMessage();
              }
            }}
            className="p-2 text-gray-500 hover:bg-gray-100 rounded-full ml-2"
            disabled={
              isUploading ||
              uploadMediaMutation.isLoading ||
              (!newMessage.trim() && !mediaFile)
            }
          >
            <Send size={20} />
          </button>
        </div>

        {/* Reply Preview */}
        {replyTo && (
          <div className="mt-2 p-2 bg-gray-50 border-l-4 border-purple-600 text-xs text-gray-600 flex items-start justify-between">
            <div className="flex-1">
              Replying to: <strong>{replyTo.senderName}</strong> in{" "}
              <em>
                {replyTo.siteName}@{replyTo.chatroomName}
              </em>
              <p className="truncate">{replyTo.text}</p>
            </div>
            <button
              onClick={() => setReplyTo(null)}
              className="ml-2 text-gray-500 hover:text-red-600"
            >
              <X size={16} />
            </button>
          </div>
        )}
      </div>

      {isFormOpen && (
        <ImageModal
          imageUrl={selectedImage}
          onClose={() => {
            setIsFormOpen(false);
            setSelectedImage(null);
          }}
        />
      )}
      {/* File Viewer Modal */}
      {viewingFile && (
        <FileViewerModal
          fileUrl={viewingFile.url}
          fileName={viewingFile.name}
          onClose={() => setViewingFile(null)}
        />
      )}
    </div>
  );
};

export default CompanyCommunications;